import { type WsBaseMessage } from '@/shared/realtime/ws-types';
import type { DeviceIconConfig } from '@/types/device-icon';
import { z } from 'zod';
import { workerMobileProjectionSchema } from '@/domains/worker-mobile/model';

export type DeviceStatusLevel = 'normal' | 'warning' | 'caution' | 'offline' | 'unknown' | 'unregistered';
export type DeviceMarkerKafkaEventLevel = 'info' | 'warning' | 'alert' | 'error';

export const deviceGroupMeasurementsSchema = z.object({
  co: z.number().nullable().optional(),
  o2: z.number().nullable().optional(),
  h2s: z.number().nullable().optional(),
  exp: z.number().nullable().optional(),
  tvoc: z.number().nullable().optional(),
  co2: z.number().nullable().optional(),
  tp: z.number().nullable().optional(),
  appr_tp: z.number().nullable().optional(),
  hm: z.number().nullable().optional(),
  pm1_0: z.number().nullable().optional(),
  pm2_5: z.number().nullable().optional(),
  pm10: z.number().nullable().optional(),
  dba: z.number().nullable().optional(),
  dbv: z.number().nullable().optional(),
  wind_spd: z.number().nullable().optional(),
  wind_deg: z.number().nullable().optional(),
  wind_dir: z.string().nullable().optional(),
  xdeg: z.number().nullable().optional(),
  ydeg: z.number().nullable().optional(),
  zdeg: z.number().nullable().optional(),
  power_operation_state: z.number().int().min(0).max(15).nullable().optional(),
  power_operation_state_name: z.string().nullable().optional(),
  power_charging_state: z.number().int().min(0).max(15).nullable().optional(),
  power_charging_state_name: z.string().nullable().optional(),
  router_power: z.number().int().min(0).max(15).nullable().optional(),
  router_timer_control: z.number().int().min(0).max(15).nullable().optional(),
  gps_on_off: z.number().int().min(0).max(15).nullable().optional(),
  gps_timer_control: z.number().int().min(0).max(15).nullable().optional(),
  indicator_led: z.number().int().min(0).max(15).nullable().optional(),
  indicator_timer_control: z.number().int().min(0).max(15).nullable().optional(),
  power_off_reason: z.number().int().min(0).max(255).nullable().optional(),
  power_off_reason_name: z.string().nullable().optional(),
});

export const deviceMarkerKafkaEventSchema = z.object({
  active: z.boolean(),
  at: z.string(),
  eventKey: z.string(),
  event: z.string(),
  label: z.string(),
  level: z.enum(['info', 'warning', 'alert', 'error']),
  message: z.string().optional(),
  autoClearAfterMs: z.number().int().positive().optional(),
  shouldAutoFocusMap: z.boolean().optional(),
  shouldAutoOpenMarkerPopup: z.boolean().optional(),
  sourceDeviceUid: z.string().optional(),
  displayDeviceUid: z.string().optional(),
  alarmUid: z.string().optional(),
  alarmVersion: z.number().int().nonnegative().optional(),
  sourceSensorVersion: z.number().int().nonnegative().optional(),
  eventType: z.string().optional(),
  eventAt: z.string().optional(),
  identities: z.array(z.string()).optional(),
  tooltip: z.string().optional(),
  topic: z.string().optional(),
});

const optionalCoordinateSchema = z.preprocess(value => (value === null ? undefined : value), z.number().optional());

export const deviceStatusSummarySchema = z.object({
  field: z.number(),
  device: z.number(),
  worker: z.number(),
  alarm: z.number(),
});

export const deviceStatusItemSchema = z.object({
  id: z.string(),
  // 선박 관측 마커에는 기존 장비 등록·수신·제어 경로를 연결하지 않습니다.
  readOnlyObservation: z.boolean().optional(),
  entityUid: z.string().optional(),
  // V2 제품 마커를 기존 업무 목록의 장비·구성 센서 식별자와 연결합니다.
  navigationUids: z.array(z.string()).optional(),
  name: z.string(),
  mobileDevice: workerMobileProjectionSchema.optional(),
  mobilePeers: z.array(workerMobileProjectionSchema).optional(),
  deviceName: z.string().optional(),
  deviceType: z.string().optional(),
  workerName: z.string().optional(),
  // 원본 센서별 지급 정보와 해제 진행 상태를 팝업에 전달합니다.
  sourceRecipients: z.record(z.string(), z.unknown()).optional(),
  warningClearAllowed: z.boolean().optional(),
  warningClearPendingKey: z.string().optional(),
  deviceCode: z.string().optional(),
  productUid: z.string().optional(),
  productName: z.string().optional(),
  productLoraType: z.number().int().optional(),
  productSensorType: z.number().int().optional(),
  productSensorTypeName: z.string().optional(),
  productSensorTypeLabel: z.string().optional(),
  isCctv: z.boolean().optional(),
  cameraUid: z.string().optional(),
  cameraStatus: z.string().optional(),
  cameraIsOn: z.boolean().optional(),
  cameraStreamHealthy: z.boolean().optional(),
  cameraEffectiveIsOn: z.boolean().optional(),
  mainStream: z.string().optional(),
  subStream: z.string().optional(),
  rId: z.string().optional(),
  sId: z.string().optional(),
  saconState: z.string().optional(),
  rawData: z.string().optional(),
  gpsValid: z.boolean().optional(),
  gpsStatus: z.string().optional(),
  status: z.enum(['normal', 'warning', 'caution', 'offline', 'unknown', 'unregistered']),
  lastSeen: z.string(),
  lastSeenAt: z.string().optional(),
  location: z.string(),
  hasInstallLocationText: z.boolean().optional(),
  fieldUid: z.string().optional(),
  lat: optionalCoordinateSchema,
  lon: optionalCoordinateSchema,
  markerIconUrl: z.string().optional(),
  deviceIcon: z.unknown().optional(),
  mapMarkerIcon: z.unknown().optional(),
  constructionEquipment: z.unknown().optional(),
  isConstructionEquipment: z.boolean().optional(),
  isEscapePreventionEnabled: z.boolean().optional(),
  batteryPercent: z.number().min(0).max(100).nullable().optional(),
  batteryVoltage: z.number().finite().positive().optional(),
  batteryStatus: z.enum(['unknown', 'low', 'normal', 'good', 'full']).optional(),
  deviceStateRaw: z.enum(['NORMAL', 'ALERT', 'WARNING', 'OFF', 'INVISUAL']).optional(),
  geofenceIsInside: z.boolean().nullable().optional(),
  escapeAlertActive: z.boolean().optional(),
  movementStatus: z.string().optional(),
  lastMovementAt: z.string().nullable().optional(),
  sensorState: z.string().optional(),
  sensorStateName: z.string().optional(),
  sensorCount: z.number().int().nonnegative().optional(),
  powerOperationState: z.number().int().min(0).max(15).optional(),
  powerOperationStateName: z.string().optional(),
  powerChargingState: z.number().int().min(0).max(15).optional(),
  powerChargingStateName: z.string().optional(),
  routerPower: z.number().int().min(0).max(15).optional(),
  routerTimerControl: z.number().int().min(0).max(15).optional(),
  gpsOnOff: z.number().int().min(0).max(15).optional(),
  gpsTimerControl: z.number().int().min(0).max(15).optional(),
  indicatorLed: z.number().int().min(0).max(15).optional(),
  indicatorTimerControl: z.number().int().min(0).max(15).optional(),
  powerOffReason: z.number().int().min(0).max(255).optional(),
  powerOffReasonName: z.string().optional(),
  connectionState: z.string().optional(),
  movementEvent: z.string().optional(),
  movementEventLabel: z.string().optional(),
  movementOffReason: z.string().optional(),
  movementOffReasonName: z.string().optional(),
  movementDisplayLabel: z.string().optional(),
  measurements: deviceGroupMeasurementsSchema.optional(),
  deviceUpdatedAt: z.string().optional(),
  deviceGroupUid: z.string().optional(),
  mainDeviceUid: z.string().optional(),
  displayDeviceUid: z.string().optional(),
  displayDeviceName: z.string().optional(),
  displayDeviceCode: z.string().optional(),
  displayRId: z.string().optional(),
  displaySId: z.string().optional(),
  displayProductUid: z.string().optional(),
  displayProductSensorType: z.number().int().optional(),
  sourceDeviceUid: z.string().optional(),
  sourceDeviceName: z.string().optional(),
  sourceDeviceCode: z.string().optional(),
  sourceRId: z.string().optional(),
  sourceSId: z.string().optional(),
  sourceProductUid: z.string().optional(),
  sourceProductSensorType: z.number().int().optional(),
  originalDeviceUid: z.string().optional(),
  originalDeviceName: z.string().optional(),
  originalDeviceCode: z.string().optional(),
  originalRId: z.string().optional(),
  originalSId: z.string().optional(),
  originalProductUid: z.string().optional(),
  originalProductSensorType: z.number().int().optional(),
  groupName: z.string().optional(),
  groupDeviceCount: z.number().int().nonnegative().optional(),
  groupTotalDeviceCount: z.number().int().positive().optional(),
  groupChildCounterCount: z.number().int().nonnegative().optional(),
  isGroupMainDevice: z.boolean().optional(),
  isGroupCounterParent: z.boolean().optional(),
  isGroupCounterChild: z.boolean().optional(),
  sourceIsGroupCounterChild: z.boolean().optional(),
  usesGroupData: z.boolean().optional(),
  kafkaEvent: deviceMarkerKafkaEventSchema.optional(),
});

export const deviceStatusSnapshotSchema = z.object({
  summary: deviceStatusSummarySchema,
  items: z.array(deviceStatusItemSchema),
  source: z.literal('sse'),
  updatedAt: z.string(),
});

/** 지도와 Tunnel 마커 popup의 운용 상태 라벨을 반환합니다. */
export function formatMapMarkerStatus(status: DeviceStatusItem['status']): string {
  const labels: Record<DeviceStatusItem['status'], string> = {
    caution: '주의',
    normal: 'ON',
    offline: 'OFF',
    unknown: '상태 미확인',
    unregistered: '미등록',
    warning: '경고',
  };
  return labels[status];
}

export type DeviceStatusSummary = z.infer<typeof deviceStatusSummarySchema>;
export type DeviceGroupMeasurements = z.infer<typeof deviceGroupMeasurementsSchema>;
export type DeviceMarkerKafkaEvent = z.infer<typeof deviceMarkerKafkaEventSchema>;
export type DeviceStatusItem = z.infer<typeof deviceStatusItemSchema>;
export type DeviceStatusSnapshot = z.infer<typeof deviceStatusSnapshotSchema>;
export type DeviceStatusItemWithIcon = DeviceStatusItem & {
  deviceIcon?: DeviceIconConfig | null;
  mapMarkerIcon?: DeviceIconConfig | null;
  markerIconUrl?: string;
};

export const DEFAULT_CONSTRUCTION_EQUIPMENT_MARKER_ICON: DeviceIconConfig = {
  provider: 'system',
  name: 'marker',
  marker_size: 72,
  size: 20,
};

/**
 * GPS 수신 실패 상태 코드인지 확인합니다.
 */
export function isGpsNoFixStatus(value: string | null | undefined): boolean {
  return (
    String(value ?? '')
      .trim()
      .toUpperCase() === 'NO_FIX'
  );
}

/**
 * 장비 상태 아이템이 GPS 미수신 이벤트를 담고 있는지 확인합니다.
 */
export function isGpsNoFixItem(item: Pick<DeviceStatusItem, 'saconState' | 'gpsValid' | 'gpsStatus'>): boolean {
  const state = String(item.saconState ?? '')
    .trim()
    .toUpperCase();
  return state === 'GPS' && (item.gpsValid === false || isGpsNoFixStatus(item.gpsStatus));
}

/**
 * 지도 팝업과 패널에 표시할 GPS 미수신 문구를 반환합니다.
 */
export function formatGpsNoFixLabel(item: Pick<DeviceStatusItem, 'saconState' | 'gpsValid' | 'gpsStatus'>): string | null {
  if (!isGpsNoFixItem(item)) {
    return null;
  }

  return `GPS 수신 실패 / ${String(item.gpsStatus || 'NO_FIX')
    .trim()
    .toUpperCase()}`;
}

/**
 * 장비 현황 위젯의 기본 빈 스냅샷을 생성합니다.
 */
export function createEmptyDeviceStatusSnapshot(): DeviceStatusSnapshot {
  return {
    summary: {
      field: 0,
      device: 0,
      worker: 0,
      alarm: 0,
    },
    items: [],
    source: 'sse',
    updatedAt: new Date().toISOString(),
  };
}

const EMPTY_DEVICE_STATUS_SNAPSHOT = createEmptyDeviceStatusSnapshot();

/** 현재 현장 조회 전 사용할 공용 빈 스냅샷입니다. 서버 상태 저장소를 만들지 않습니다. */
export function getEmptyDeviceStatusSnapshot(): DeviceStatusSnapshot {
  return EMPTY_DEVICE_STATUS_SNAPSHOT;
}

/**
 * 루트 또는 body에 포함된 field UID를 문자열로 정규화합니다.
 */
export function resolveMessageFieldUid(message: WsBaseMessage): string | null {
  const rootFieldUid = readString((message as WsBaseMessage & { field_uid?: unknown }).field_uid) ?? readString((message as WsBaseMessage & { fieldUid?: unknown }).fieldUid);
  if (rootFieldUid) {
    return rootFieldUid;
  }

  const body = (message.body ?? {}) as Record<string, unknown>;
  return readString(body.field_uid) ?? readString(body.fieldUid);
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
